import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { findTabbableDescendants } from 'utils/accessibility/tabbable';
import { scopeTab } from 'utils/accessibility/scopeTab';
import { scrollLock } from 'utils/scrollLock';
import { types } from '@ehi/global-marketing-interface/utils/js/types';
import { KEYS, MODAL_THEMES, BREAKPOINTS, MODAL } from 'constants';
import Picture from 'components/Picture';
import ClearAllButton from 'components/SortFilterWrapper/ClearAllButton';
import Button from 'components/Button';
import { ModalPropTypes, ModalDefaultProps } from './modalPropTypes';

const themeClasses = {
  [MODAL_THEMES.BLUE]: 'theme--light-blue',
  [MODAL_THEMES.WHITE]: 'theme--light',
};

/**
 * Modal
 * @module
 * @author yauheni.mukavozchyk@isobar.com
 * Usage:
 *    <Modal
 *      modalKey='my_unique_modal_key'
 *      title='My Modal'
 *      {'my modal content'}
 *    </Modal>
 */

const Modal = (props) => {
  const {
    isOpen,
    breakpoint,
    finalAction,
    clearModalQueue,
    onClose,
    contentProps = {},
    header,
    shouldRenderHeaderCloseButton,
    headerCloseAriaLabel,
    showHeaderIcon,
    showHeaderImg,
    headerImg = {},
    headerIcons = {},
    dataDtmTrackOpen,
    dataDtmTrackClose,
    customActionOnClose,
    modalKey,
    clearFilter,
    headerRowWithClearButton,
    showHeaderTextIcon,
    headerTextIcons = {},
    headerIconBgColor,
    secondIcon,
    analyticsKey = modalKey,
    showHeaderCTA,
    isExpanded,
    handleHeaderCTA,
    headerCtaTitle,
    children,
    customClass,
    theme,
    role,
    descId,
    handleClearAll,
    handleModalQueue,
    shouldCloseOnEsc,
    closeModal,
  } = props;

  const [stickyHeader, setStickyHeader] = useState(false);
  const [stickyTriggerElementStartingPosition, setStickyTriggerElementStartingPosition] = useState(null);

  const modalContainerRef = useRef();
  const modalFocus = useRef();
  const headerText = useRef();
  const headerCloseButton = useRef();
  const modalContentRef = useRef();
  const lastFocusedElRef = useRef(null);
  const prevBreakpointRef = useRef();

  // Handle modal open/close effects
  useEffect(() => {
    if (modalKey !== MODAL.SESSION_TIMEOUT_MODAL && isOpen) {
      // on open
      // - store last focused element
      // - focus modal body
      lastFocusedElRef.current = document.activeElement;

      const modalEl = modalContentRef.current;
      modalEl && findTabbableDescendants(modalEl)[0].focus();

      headerCloseButton?.current?.focus();
    } else if (!isOpen && lastFocusedElRef.current) {
      // - on close
      // - return focus
      lastFocusedElRef.current.focus();

      if (onClose) {
        onClose();
      }

      if (finalAction) {
        finalAction();
        clearModalQueue({ skipAnalytics: true });
      }

      if (stickyHeader) {
        setStickyHeader(false);
      }
    }

    // TODO: Need to QA if any modals have mobile software keyboards and if this causes issues inside the modal
    scrollLock({ toggleValue: isOpen });
    isOpen
      ? document.body.classList.add('modal-open-print-styles')
      : document.body.classList.remove('modal-open-print-styles');

    // Cleanup function for unmounting
    return () => {
      if (isOpen) {
        scrollLock({ toggleValue: false });
        document.body.classList.remove('modal-open-print-styles');
      }
    };
  }, [isOpen, modalKey, onClose, finalAction, clearModalQueue, stickyHeader]);

  // Handle breakpoint changes
  useEffect(() => {
    const prevBreakpoint = prevBreakpointRef.current;

    if (typeof prevBreakpoint !== 'undefined') {
      // Skip on first render
      const breakpointSmallTransition =
        (breakpoint === BREAKPOINTS.SMALL || prevBreakpoint === BREAKPOINTS.SMALL) && breakpoint !== prevBreakpoint;

      if (breakpointSmallTransition) {
        setStickyHeader(false);
        setStickyTriggerElementStartingPosition(null);
      }
    }

    // Update prev breakpoint after checking
    prevBreakpointRef.current = breakpoint;
  }, [breakpoint]);

  const handleKeyDown = (e) => {
    if (e.keyCode === KEYS.TAB) {
      const modalEl = modalContentRef.current;
      modalEl && scopeTab(modalEl, e);
    } else if (shouldCloseOnEsc && e.keyCode === KEYS.ESCAPE) {
      e.stopPropagation();
      clearModalQueue();
    }
  };

  // handleClose and handleClearModalQueue are designed to prevent dom events from making it through to the redux
  // actions if Modal children pass the actions directly to domElements. This is because we should avoid passing all
  // those props from the event objects to the reducers and middleware for fear of potentially weird naming collisions
  const handleClose = (args) => closeModal(args?.nativeEvent ? null : args);

  // Checking for prop 'handleModalQueue' with the `skipAnalytics` flag is set to `true` // handleModalQueue={{skipAnalytics: true}}
  const handleClearModalQueue = (args) => {
    if (handleModalQueue?.skipAnalytics) {
      clearModalQueue({ skipAnalytics: true });
    } else {
      clearModalQueue(args?.nativeEvent ? null : args);
    }
  };

  const handleScroll = (e) => {
    //  If the user is scrolling in the date picker or the time selector
    //  we don't want to change the modal.  We need the isScrollingOnDatePicker var due to changes on scrolling
    // in mobile
    const isScrollingOnDatePickerDesktop = e.target?.classList?.contains('react-datepicker');
    const isScrollingInTimeSelector = e.target.dataset.isTimeSelector;
    const stickyTriggerElement =
      breakpoint !== BREAKPOINTS.SMALL ? headerCloseButton.current || headerText.current : headerText.current;
    if (stickyTriggerElement && !(isScrollingInTimeSelector || isScrollingOnDatePickerDesktop)) {
      const scrollPosition = e.target.scrollTop;
      let startingPosition = stickyTriggerElementStartingPosition;

      if (!startingPosition) {
        const { top } = stickyTriggerElement.getBoundingClientRect();
        startingPosition = top + scrollPosition;
        setStickyTriggerElementStartingPosition(startingPosition);
        // Saving the starting position of the stickyTriggerElement allows us to be more flexible with the Modal layout
        // styling, as opposed to having to keep a hardcoded stickyTriggerScrollPosition value in sync with Modal layout
      }

      const stickyTriggerScrollPosition =
        breakpoint !== BREAKPOINTS.SMALL
          ? startingPosition - stickyTriggerElement.offsetTop
          : Math.abs(startingPosition - 46);
      // This subtracted value is a magic number based on trial & error of what looks good on mobile, so need to re-evaluate if styles change
      // Math.abs will ensure the figure is always positive, especially in unique cases where the startingPosition is less than the magic number
      if (scrollPosition >= stickyTriggerScrollPosition && !stickyHeader) {
        setStickyHeader(true);
      } else if (scrollPosition < stickyTriggerScrollPosition && stickyHeader) {
        setStickyHeader(false);
      }
    }
  };

  // scrollToModalTop scrolls to top of modal which is helpful in cases such as if a modal includes an error or
  // notification at the top that we want to highlight but the modal may have scrolled out of sight of the notification.
  const scrollToModalTop = () => {
    const modalScrollContainer = modalContainerRef?.current;
    const scrollAnimation = () => {
      if (modalScrollContainer && modalScrollContainer.scrollTop !== 0) {
        modalScrollContainer.scrollTop -= modalScrollContainer.scrollTop / 8;
        // The 8 above is a magic number from trial and error for getting a smooth scroll. This recursive
        // requestAnimationFrame method is best option when the scroll animation may be interrupted by component
        // re-renders.
        requestAnimationFrame(scrollAnimation);
      }
    };
    requestAnimationFrame(scrollAnimation);
  };

  const handleClearAllAction = () => {
    handleClearAll();
  };

  const renderHeader = ({ hidden } = {}) => {
    // Passing {hidden: true} option renders an invisible header used to preserve layout when main header becomes fixed
    const headerTextContent = contentProps.header || header; // Header text passed in through openModal props overrides
    const clearFilterText = clearFilter;
    const onMobile = breakpoint === BREAKPOINTS.SMALL;
    const headerAttrs =
      (dataDtmTrackOpen && {
        'data-dtm-track': dataDtmTrackOpen,
      }) ||
      {};
    const closeBtnAttrs =
      (dataDtmTrackClose && {
        'data-dtm-track': dataDtmTrackClose,
      }) ||
      {};

    const headerCloseButtonElement = !hidden && shouldRenderHeaderCloseButton && (
      <Button
        plain
        className={cn('modal__close-btn', { 'modal__close-btn--sticky': stickyHeader })}
        aria-label={headerCloseAriaLabel}
        onClick={() => {
          customActionOnClose?.();

          /*
           * This allows the analyticsKey to be different from the modalKey. If none is provided, it defaults to the
           * modalKey (see destructuring of props above).
           */
          const args = {
            analyticsKey,
          };

          handleClearModalQueue(args);
        }}
        buttonRef={headerCloseButton}
        type='button'
        {...closeBtnAttrs}
      />
    );

    const headerIcon = showHeaderIcon && (
      <Picture
        className={headerIcons.className}
        src={headerIcons.src}
        srcTablet={headerIcons.srcTablet}
        srcMobile={headerIcons.srcMobile}
        alt={headerIcons.alt}
      />
    );

    const headerTextIcon = showHeaderTextIcon && (
      <div className={`modal__header-text-icon ${headerIconBgColor} ${headerTextIcons}`} />
    );

    // Conditional second icon specifically for flight update cancellation modal
    const secondHeaderIcon = showHeaderTextIcon && secondIcon && (
      <div className={'modal__header-text-icon-error-icon'} />
    );

    const modalHeaderImg = showHeaderImg && (
      <img className={headerImg.className} src={headerImg.src} alt={headerImg.alt} />
    );

    return (
      <>
        <div
          className={cn('modal__header', {
            'modal__header--sticky': !hidden && stickyHeader,
            'modal__header--layout-placeholder': hidden,
          })}
          {...headerAttrs}
        >
          {!onMobile && headerCloseButtonElement}

          {headerIcon}

          {modalHeaderImg}

          {headerTextIcon}
          {secondHeaderIcon}

          {onMobile && !stickyHeader && <div className='modal__header__mobile-scroll-blocker' />}
          <div className={cn({ modal__header_row: headerRowWithClearButton })}>
            {headerTextContent && (
              <div>
                <h2
                  id={`${modalKey}_label`}
                  ref={!hidden ? headerText : null}
                  className={cn('modal-themed__header', {
                    'no-margin-top': headerTextIcon,
                  })}
                  aria-hidden={!!hidden}
                >
                  {headerTextContent}
                </h2>
              </div>
            )}
            {showHeaderCTA && (
              <Button
                plain
                onClick={handleHeaderCTA}
                aria-label={headerCtaTitle}
                aria-expanded={isExpanded}
                className={cn('modal-themed__header-cta', 'modal-themed__header-cta-draw-icon', {
                  'modal-themed__header-cta-draw-icon--open': isExpanded,
                })}
              >
                {headerCtaTitle}
              </Button>
            )}

            {clearFilterText && (
              <div className='modal-themed__left_header'>
                <ClearAllButton handleClearAll={handleClearAllAction} customClass='custom-filter-clear-all' />
              </div>
            )}
          </div>
        </div>
        {onMobile && headerCloseButtonElement}
        {/* close button needs to be outside header in mobile to avoid weird bug with sticky header */}
      </>
    );
  };

  const renderChildren = (childrenContent, modalFunctions, contentPropsData) => {
    if (types(childrenContent).isFunction) {
      return childrenContent({ ...contentPropsData, ...modalFunctions });
    }

    // if children.type is a string, it is referencing a raw Dom element so passing props to it would cause
    // a react warning
    if (types(childrenContent.type).isString) {
      return childrenContent;
    }

    return React.cloneElement(childrenContent, Object.assign(modalFunctions, contentPropsData));
  };

  if (!isOpen) {
    // modal is always "mounted" but returning "null" would not affect rendering perf since there is no actual node to mount/unmount
    return null;
  }

  const themeClass = themeClasses[theme];
  const modalClasses = cn('modal-overlay', {
    [customClass]: customClass,
  });

  const modalFocusEl = <div ref={modalFocus} tabIndex={-1} onKeyDown={handleKeyDown} aria-hidden={true} />;
  // when modal extends below screen, initial focus would scroll down to top of modal. Using this element to keep focus at top

  const modalFunctions = {
    handleClose,
    clearModalQueue: handleClearModalQueue,
    modalKey,
    scrollToModalTop,
  };

  const modal = (
    <div className={modalClasses} onScroll={handleScroll} ref={modalContainerRef}>
      {modalFocusEl}
      <div
        className='modal'
        aria-modal={true}
        id={modalKey}
        tabIndex={-1}
        role={role}
        aria-labelledby={`${modalKey}_label`}
        aria-describedby={descId || `${modalKey}_desc`}
        ref={modalContentRef}
        onKeyDown={handleKeyDown}
      >
        <div className={cn('modal__body', { [themeClass]: themeClass })}>
          {renderHeader()}
          {stickyHeader && renderHeader({ hidden: true })}

          <div className='modal__content'>{renderChildren(children, modalFunctions, contentProps)}</div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

Modal.propTypes = ModalPropTypes;
Modal.defaultProps = ModalDefaultProps;

export default Modal;
